import React, { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";

import ObjectsMap from "../../components/map/SewerObjectsMap";
import LoadingSpinner from "../../components/loading-spinner/LoadingSpinner";
import { ActionsSection } from "../../components/data-components/sections/DataSections";
import { SewerObjectsMapStorage } from "../../services/sewer-object/SewerObjectsMapStorage";
import { translationService } from "../../services/translation/TranslationService";

export const sewerObjectsMapIsCollapsedKey = "sewerObjectsMapIsCollapsed";
interface SewerObjectsMapProps {
  sewerObjectsMapStorage: SewerObjectsMapStorage;
}

export default observer(function SewerObjectsMap(props: SewerObjectsMapProps) {
  const { sewerObjectsMapStorage } = props;

  useEffect(() => {
    if (!sewerObjectsMapStorage.isDataLoading) {
      sewerObjectsMapStorage.setMapNeedsRecomputation(true);
    }
  }, [sewerObjectsMapStorage, sewerObjectsMapStorage.isDataLoading]);

  const mapData = useMemo(() => {
    if (sewerObjectsMapStorage.mapNeedsRecomputation) {
      return (
        <>
          {sewerObjectsMapStorage.allSewerObjectsAreWithoutGeometry && (
            <ActionsSection
              type="question"
              noBorder
              isSticky={false}
              warningKey="ViewSewerObjectsOnMap.allSewerObjectsWithoutGeometryWarning"
            />
          )}
          {sewerObjectsMapStorage.someSewerObjectsAreWithoutGeometry && (
            <>
              <ActionsSection
                type="question"
                noBorder
                isSticky={false}
                warningKey={
                  sewerObjectsMapStorage.oneSewerObjectIsWithoutGeometry
                    ? "ViewSewerObjectsOnMap.oneSewerObjectWithoutGeometryWarning"
                    : "ViewSewerObjectsOnMap.someSewerObjectsWithoutGeometryWarning"
                }
                warningContext={
                  sewerObjectsMapStorage.oneSewerObjectIsWithoutGeometry
                    ? undefined
                    : {
                        count: sewerObjectsMapStorage.numberOfSewerObjectsWithoutGeometry,
                      }
                }
              />
              {sewerObjectsMapStorage.isItemsBoundingBoxTooLarge && (
                <ActionsSection
                  noBorder
                  isSticky={false}
                  warningKey="ViewSewerObjectsOnMap.verifyCoordinateSystem"
                />
              )}
              <ObjectsMap sewerObjectsMapStorage={sewerObjectsMapStorage} />
            </>
          )}
          {sewerObjectsMapStorage.allSewerObjectsAreWithGeometry && (
            <>
              {sewerObjectsMapStorage.isItemsBoundingBoxTooLarge && (
                <ActionsSection
                  noBorder
                  isSticky={false}
                  warningKey="ViewSewerObjectsOnMap.verifyCoordinateSystem"
                />
              )}
              <ObjectsMap sewerObjectsMapStorage={sewerObjectsMapStorage} />
            </>
          )}
        </>
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sewerObjectsMapStorage.mapNeedsRecomputation]);

  if (sewerObjectsMapStorage.isDataLoading) {
    return <LoadingSpinner />;
  } else {
    if (sewerObjectsMapStorage.noVisibleSewerObjects)
      return (
        <ActionsSection
          noBorder
          isSticky={false}
          warningKey="general.list.no-items-matching-criteria"
          warningContext={{ itemType: translationService.translate("general.sewer-objects") }}
        />
      );
    else return <>{mapData}</>;
  }
});
